import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { systemService } from '../../services/api';
import Card from '../common/Card';
import Button from '../common/Button';

// Styled components
const SystemInfoContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const SystemInfoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  color: ${({ theme }) => theme.colors.dark};
  margin: 0;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
`;

const InfoCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing[4]};
`;

const InfoCardTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  color: ${({ theme }) => theme.colors.dark};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
`;

const InfoCardValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.primary};
`;

const InfoList = styled.ul`
  padding: 0;
  margin: ${({ theme }) => theme.spacing[4]} 0 0 0;
  list-style: none;
`;

const InfoListItem = styled.li`
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[2]} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: ${({ theme }) => theme.colors.medium};
`;

const InfoValue = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const StatusIndicator = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${({ status, theme }) => 
    status === 'online' ? theme.colors.success :
    status === 'warning' ? theme.colors.warning :
    theme.colors.error
  };
  margin-right: ${({ theme }) => theme.spacing[2]};
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const SystemInfo = () => {
  const [systemInfo, setSystemInfo] = useState({ status: 'loading' });

  const fetchSystemInfo = async () => {
    try {
      const response = await systemService.getSystemInfo();
      setSystemInfo(response.data || {});
    } catch (error) {
      console.error('Error fetching system info:', error);
    }
  };

  // Fetch system info on component mount and periodically
  useEffect(() => {
    // Fetch immediately on mount
    fetchSystemInfo();

    // Then set up interval to refresh every 30 seconds
    const interval = setInterval(fetchSystemInfo, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    fetchSystemInfo();
  };

  const handleRestart = async () => {
    if (window.confirm('Are you sure you want to restart the system? This will disconnect all users temporarily.')) {
      try {
        const response = await systemService.restartSystem();
        const result = response.data || {};
        alert(`${result.message || 'Restart initiated'}. ${result.estimatedTimeToRestart || 'A moment'} until the system is back online.`);
      } catch (error) {
        console.error('Error restarting system:', error);
        alert('Failed to restart system. Please try again.');
      }
    }
  };
  
  return (
    <SystemInfoContainer>
      <SystemInfoHeader>
        <Title>System Information</Title>
        <ButtonsContainer>
          <Button variant="outline" onClick={handleRefresh}>
            Refresh
          </Button>
          <Button variant="secondary" onClick={handleRestart}>
            Restart System
          </Button>
        </ButtonsContainer>
      </SystemInfoHeader>
      
      <InfoGrid>
        <InfoCard>
          <InfoCardTitle>System Status</InfoCardTitle>
          <InfoCardValue>
            <StatusIndicator status={systemInfo.status} />
            {systemInfo.status === 'online' ? 'Online' : 
             systemInfo.status === 'warning' ? 'Warning' : 
             systemInfo.status === 'loading' ? 'Loading...' : 'Offline'}
          </InfoCardValue>
          <InfoList>
            <InfoListItem>
              <InfoLabel>Version</InfoLabel>
              <InfoValue>{systemInfo.version || '—'}</InfoValue>
            </InfoListItem>
            <InfoListItem>
              <InfoLabel>Last Updated</InfoLabel>
              <InfoValue>{systemInfo.lastUpdated ? new Date(systemInfo.lastUpdated).toLocaleString() : '—'}</InfoValue>
            </InfoListItem>
            <InfoListItem>
              <InfoLabel>Uptime</InfoLabel>
              <InfoValue>{systemInfo.uptime || '—'}</InfoValue>
            </InfoListItem>
          </InfoList>
        </InfoCard>

        <InfoCard>
          <InfoCardTitle>Server Load</InfoCardTitle>
          <InfoList>
            <InfoListItem>
              <InfoLabel>CPU Usage</InfoLabel>
              <InfoValue>{systemInfo.server ? `${systemInfo.server.cpu}%` : '—'}</InfoValue>
            </InfoListItem>
            <InfoListItem>
              <InfoLabel>Memory Usage</InfoLabel>
              <InfoValue>{systemInfo.server ? `${systemInfo.server.memory}%` : '—'}</InfoValue>
            </InfoListItem>
            <InfoListItem>
              <InfoLabel>Disk Usage</InfoLabel>
              <InfoValue>{systemInfo.server ? `${systemInfo.server.disk}%` : '—'}</InfoValue>
            </InfoListItem>
            <InfoListItem>
              <InfoLabel>Database Status</InfoLabel>
              <InfoValue>
                <StatusIndicator status={systemInfo.database ? systemInfo.database.status : 'loading'} />
                {systemInfo.database && systemInfo.database.status === 'online' ? 'Online' :
                 systemInfo.database && systemInfo.database.status === 'warning' ? 'Warning' :
                 systemInfo.database && systemInfo.database.status === 'offline' ? 'Offline' : '—'}
              </InfoValue>
            </InfoListItem>
          </InfoList>
        </InfoCard>

        <InfoCard>
          <InfoCardTitle>Storage</InfoCardTitle>
          <InfoCardValue>{systemInfo.storage ? systemInfo.storage.used : '—'}</InfoCardValue>
          <InfoList>
            <InfoListItem>
              <InfoLabel>Total Capacity</InfoLabel>
              <InfoValue>{systemInfo.storage ? systemInfo.storage.total : '—'}</InfoValue>
            </InfoListItem>
            <InfoListItem>
              <InfoLabel>Usage</InfoLabel>
              <InfoValue>{systemInfo.storage ? `${systemInfo.storage.percent}%` : '—'}</InfoValue>
            </InfoListItem>
          </InfoList>
        </InfoCard>

        <InfoCard>
          <InfoCardTitle>Users</InfoCardTitle>
          <InfoCardValue>{systemInfo.users ? systemInfo.users.active : '—'} active</InfoCardValue>
          <InfoList>
            <InfoListItem>
              <InfoLabel>Total Users</InfoLabel>
              <InfoValue>{systemInfo.users ? systemInfo.users.total : '—'}</InfoValue>
            </InfoListItem>
            <InfoListItem>
              <InfoLabel>License Type</InfoLabel>
              <InfoValue>{systemInfo.license ? systemInfo.license.type : '—'}</InfoValue>
            </InfoListItem>
            <InfoListItem>
              <InfoLabel>License Expiry</InfoLabel>
              <InfoValue>{systemInfo.license ? systemInfo.license.expiry : '—'}</InfoValue>
            </InfoListItem>
          </InfoList>
        </InfoCard>
      </InfoGrid>
    </SystemInfoContainer>
  );
};

export default SystemInfo;